/**
 * `mapEntities.ts` — collapse the raw Dynamo DTOs
 * (`EntitySnapshotDto[]`, `EntitySettingsDto[]`, plus a `partId →
 * assetId` map populated from `PartDto[]` per asset) into the
 * `EntityData[]` the inspector / hierarchy / renderer already
 * consume.
 *
 * Mirrors `ProjectDataModel.LoadSceneAsync` lines 988–1012:
 *
 *     foreach (var kvp in entityIDToEntitySnapshots) {
 *         EntitySnapshotData rootSnap = kvp.Value.First();
 *         string partID = rootSnap.PartID;
 *         string assetID = context.GetAssetIDByPartID(partID);
 *         …
 *         var settings = entityIDToEntitySettings[entityID] ?? new EntitySettingsData(entityID);
 *         var entity = new EntityData(assetID, scene.ID, entityID, settings, snapshots);
 *     }
 *
 * Snapshots that don't resolve to an `assetId` are dropped (Unity
 * logs a warning in the same place — we do too via `console.warn` at
 * the call site).
 *
 * Snapshot parent/child relationships are rebuilt from `PartData.HierachyID`,
 * the same child-index path Unity uses when walking the imported transform
 * hierarchy. Sorting by that path keeps the same model's part order stable
 * across scenes even when Dynamo returns snapshot rows in a different order.
 */

import { readAliases } from "./aliasData";
import {
  DEFAULT_ENTITY_ANIMATION_SETTINGS,
  ENTITY_ANIMATION_PLAYBACK_TYPE,
  type EntityAnimationPlaybackType,
  type EntityAnimationSettings,
  type EntityData,
  type EntityKind,
  type EntitySnapshot,
  type EntityTransform,
} from "./entityData";
import type { EntitySettingsDto } from "./entitySettingsDto";
import type { EntitySnapshotDto } from "./entitySnapshotDto";
import { normalizeExplosionFactor } from "./explosionFactor";
import { toNameFloatMap, vec3From } from "./nameFloatMap";

export interface MapEntitiesInput {
  readonly sceneId: string;
  readonly snapshots: ReadonlyArray<EntitySnapshotDto>;
  readonly settings: ReadonlyArray<EntitySettingsDto>;
  /** Built by walking each asset's `PartDto[]`: `partId → assetId`. */
  readonly partIdToAssetId: ReadonlyMap<string, string>;
  /** Built from `PartDto.HierachyID`: `partId → hierarchyPath`. */
  readonly partHierarchyById?: ReadonlyMap<string, string>;
  /** Built from `PartDto` import TRS: `partId → original local transform`. */
  readonly partTransformById?: ReadonlyMap<string, EntityTransform>;
}

interface MutableEntitySnapshot extends Omit<EntitySnapshot, "children"> {
  children: MutableEntitySnapshot[];
}

export function mapEntities(input: MapEntitiesInput): EntityData[] {
  const {
    sceneId,
    snapshots,
    settings,
    partIdToAssetId,
    partHierarchyById,
    partTransformById,
  } = input;

  // Group snapshots by EntityID
  const byEntity = new Map<string, EntitySnapshotDto[]>();
  for (const snap of snapshots) {
    if (!snap?.EntityID) continue;
    const list = byEntity.get(snap.EntityID) ?? [];
    list.push(snap);
    byEntity.set(snap.EntityID, list);
  }

  // Index settings rows for fast join
  const settingsById = new Map<string, EntitySettingsDto>();
  for (const s of settings) {
    if (s?.EntityID) settingsById.set(s.EntityID, s);
  }

  const out: EntityData[] = [];
  for (const [entityId, entitySnaps] of byEntity) {
    const entitySettings = settingsById.get(entityId);
    // Pick a canonical root snapshot for asset resolution.
    // Unity uses `kvp.Value.First()` (`ProjectDataModel.cs:993`); we
    // prefer an `IsRoot === true` snapshot when available because
    // ordering off the wire isn't guaranteed to put the root first.
    const rootSnap =
      entitySnaps.find((s) => s.IsRoot === true) ?? entitySnaps[0];
    const partId = rootSnap?.PartID ?? "";
    const assetId = partIdToAssetId.get(partId) ?? null;

    out.push({
      id: entityId,
      sceneId,
      assetId,
      displayOrder: parseDisplayOrder(entitySettings?.DisplayOrder),
      animationSettings: mapAnimationSettings(entitySettings),
      snapshots: buildSnapshotTree(entitySnaps, partHierarchyById, partTransformById),
    });

    // Quiet log for missing assetId — same intent as Unity's
    // `Debug.LogWarning("assetID is null")` (`ProjectDataModel.cs:998`).
    if (!assetId && partId) {
      console.warn(
        `[mapEntities] no assetId for partId=${partId} (entity=${entityId}, scene=${sceneId})`,
      );
    }
  }
  // `DisplayOrder` ALONE, stably — `Array.prototype.sort` has been stable since
  // ES2019, and `byEntity` preserves wire order, so ties keep the order the
  // rows arrived in. That is exactly what Unity's
  // `scene.Entities.OrderBy(e => e.EntitySettings.DisplayOrder)` does:
  // `SceneToData.Entities` is a `Dictionary.Values` in the same
  // first-appearance-per-`EntityID` order, and LINQ's `OrderBy` is stable too.
  //
  // Do NOT add an id tie-break here. Ties are common (every entity whose
  // settings row is missing reads 0), and any secondary key Unity doesn't
  // share reseats those entities — which renumbers the root segment of every
  // part path after them.
  return out.sort((a, b) => a.displayOrder - b.displayOrder);
}

function mapSnapshot(
  dto: EntitySnapshotDto,
  hierachyID: string | null,
  partTransformById?: ReadonlyMap<string, EntityTransform>,
): EntitySnapshot {
  const pos = toNameFloatMap(dto.Position);
  const rot = toNameFloatMap(dto.Rotation);
  const sca = toNameFloatMap(dto.Scale);
  const initialPos = toNameFloatMap(dto.InitialPos);
  const initialRot = toNameFloatMap(dto.InitialRot);

  // Snapshot id mirrors `EntitySnapshotData.ID` getter
  // (`EntitySnapshotData.cs:301-304`):
  //   string.IsNullOrEmpty(SnapshotID) ? PartID : SnapshotID
  const id = dto.SnapshotID && dto.SnapshotID.length > 0
    ? dto.SnapshotID
    : (dto.PartID ?? "");
  const partTransform = dto.PartID ? partTransformById?.get(dto.PartID) : undefined;
  const transform = {
    position: vec3From(pos, [0, 0, 0]),
    rotation: vec3From(rot, [0, 0, 0]),
    scale: vec3From(sca, [1, 1, 1]),
  };

  return {
    id,
    name: dto.Name ?? "",
    displayName: dto.DisplayName ?? dto.Name ?? "",
    kind: kindOf(dto),
    partId: dto.PartID && dto.PartID.length > 0 ? dto.PartID : null,
    isRoot: dto.IsRoot === true,
    hasMergedChildren: dto.HasMergedChildren === true,
    visibility: dto.Visibility !== false,
    forcedVisibility: dto.ForcedVisibility === true,
    isLocked: dto.IsLock === true,
    isCollisionEnabled: dto.IsCollisionEnabled === true,
    explosionFactor: normalizeExplosionFactor(dto.ExplosionFactor),
    transform,
    // Unity `TryGetHardResetPose` (`ProjectModel_Utils.cs`) prefers the
    // library `PartData` pose over the snapshot's `InitialPos`/`InitialRot`;
    // scale mirrors `GetOriginalScale` = `PartData.GetScale()` with a
    // `Vector3.one` fallback (bulk reset restores the original imported
    // scale, not [1,1,1] — 2026-07 Eva merge).
    resetTransform: {
      position: partTransform?.position
        ?? (hasVec3(initialPos) ? vec3From(initialPos, [0, 0, 0]) : transform.position),
      rotation: partTransform?.rotation
        ?? (hasVec3(initialRot) ? vec3From(initialRot, [0, 0, 0]) : transform.rotation),
      scale: partTransform?.scale ?? [1, 1, 1],
    },
    materialIds: dto.MaterialIDs ?? [],
    // Constant-spin payload stored verbatim (`PersistentSpinJson` S attr);
    // normalized to null when absent/blank so "no spin" is one shape.
    persistentSpinJson:
      typeof dto.PersistentSpinJson === "string" && dto.PersistentSpinJson.trim()
        ? dto.PersistentSpinJson
        : null,
    // Persistent-highlight payload, same verbatim + null-when-blank contract.
    persistentHighlightJson:
      typeof dto.PersistentHighlightJson === "string" && dto.PersistentHighlightJson.trim()
        ? dto.PersistentHighlightJson
        : null,
    // Per-project part name aliases; `readAliases` normalizes an absent or
    // malformed attribute to `[]` so consumers never branch on null.
    aliases: readAliases(dto.Aliases),
    // Library sync watermark (root rows). Absent stays absent — see the DTO
    // comment: 0 would mean "behind since the beginning of time".
    librarySyncedVersion:
      typeof dto.LibrarySyncedVersion === "number" && Number.isFinite(dto.LibrarySyncedVersion)
        ? dto.LibrarySyncedVersion
        : undefined,
    children: [],
    hierachyID,
  };
}

function mapSnapshotMutable(
  dto: EntitySnapshotDto,
  hierachyID: string | null,
  partTransformById?: ReadonlyMap<string, EntityTransform>,
): MutableEntitySnapshot {
  return mapSnapshot(dto, hierachyID, partTransformById) as MutableEntitySnapshot;
}

function buildSnapshotTree(
  entitySnaps: ReadonlyArray<EntitySnapshotDto>,
  partHierarchyById: ReadonlyMap<string, string> | undefined,
  partTransformById?: ReadonlyMap<string, EntityTransform>,
): ReadonlyArray<EntitySnapshot> {
  const mapped = entitySnaps.map((s, wireIndex) => {
    const rawHier = rawHierachyID(s, partHierarchyById);
    return {
      dto: s,
      snap: mapSnapshotMutable(s, rawHier, partTransformById),
      path: normalizeHierarchyPath(rawHier),
      pathSegments: hierarchyPathSegments(rawHier),
      wireIndex,
    };
  }).sort(compareMappedSnapshots);

  const roots = mapped.filter((m) => m.dto.IsRoot === true);
  const parts = mapped.filter((m) => m.dto.IsRoot !== true);

  // No root snapshot on the wire is unexpected, but keep the hierarchy usable.
  if (roots.length === 0) {
    return mapped.map((m) => m.snap);
  }

  // Prefer a single root snapshot; extra "roots" are kept as top-level peers.
  const topLevel: MutableEntitySnapshot[] = roots.map((m) => m.snap);
  const byPath = new Map<string, MutableEntitySnapshot>();
  for (const part of parts) {
    if (part.path) byPath.set(part.path, part.snap);
  }

  for (const part of parts) {
    const parent = findParentSnapshot(part.path, byPath, roots[0].snap);
    parent.children.push(part.snap);
  }

  return topLevel;
}

type MappedSnapshot = {
  readonly dto: EntitySnapshotDto;
  readonly snap: MutableEntitySnapshot;
  readonly path: string | null;
  readonly pathSegments: ReadonlyArray<number>;
  readonly wireIndex: number;
};

function compareMappedSnapshots(a: MappedSnapshot, b: MappedSnapshot): number {
  const path = compareHierarchyPath(a.pathSegments, b.pathSegments);
  if (path !== 0) return path;
  const name = a.snap.name.localeCompare(b.snap.name);
  if (name !== 0) return name;
  return a.wireIndex - b.wireIndex;
}

function compareHierarchyPath(
  a: ReadonlyArray<number>,
  b: ReadonlyArray<number>,
): number {
  if (a.length === 0 && b.length > 0) return -1;
  if (b.length === 0 && a.length > 0) return 1;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const diff = a[i] - b[i];
    if (diff !== 0) return diff;
  }
  return a.length - b.length;
}

function hierarchyPathSegments(path: string | null | undefined): ReadonlyArray<number> {
  return normalizeHierarchyPath(path)
    ?.split(",")
    .filter(Boolean)
    .map((segment) => Number.parseInt(segment, 10))
    .filter(Number.isFinite) ?? [];
}

function parseDisplayOrder(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function mapAnimationSettings(
  settings: EntitySettingsDto | undefined,
): EntityAnimationSettings {
  const nested = settings?.AnimationSettings;
  const autoplay = parseBoolean(
    settings?.AnimationAutoplay ?? nested?.autoplay ?? nested?.Autoplay,
    DEFAULT_ENTITY_ANIMATION_SETTINGS.autoplay,
  );
  const playbackType = parseAnimationPlaybackType(
    settings?.AnimationPlaybackType ?? nested?.playbackType ?? nested?.playback,
  );
  const speedValue = parseFiniteNumber(
    settings?.AnimationSpeed ?? nested?.speed,
    DEFAULT_ENTITY_ANIMATION_SETTINGS.speed,
  );
  const speedPercent = parseFiniteNumber(nested?.speedPercent, Number.NaN);
  const direction = typeof nested?.direction === "string"
    ? nested.direction.trim().toLowerCase()
    : "";
  const speed = Number.isFinite(speedPercent)
    ? Math.abs(speedPercent * 0.01) * (direction === "backward" ? -1 : 1)
    : speedValue;
  const clipIndex = Math.trunc(parseFiniteNumber(
    settings?.AnimationClipIndex ?? nested?.clipIndex ?? nested?.take,
    DEFAULT_ENTITY_ANIMATION_SETTINGS.clipIndex,
  ));
  return { autoplay, playbackType, speed, clipIndex };
}

function parseAnimationPlaybackType(value: unknown): EntityAnimationPlaybackType {
  if (typeof value === "number" && Number.isFinite(value)) {
    const playbackType = Math.trunc(value);
    if (
      playbackType === ENTITY_ANIMATION_PLAYBACK_TYPE.Once
      || playbackType === ENTITY_ANIMATION_PLAYBACK_TYPE.PingPong
    ) {
      return playbackType;
    }
    return ENTITY_ANIMATION_PLAYBACK_TYPE.Loop;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase().replace(/[_ -]/g, "");
    if (normalized === "1" || normalized === "once") {
      return ENTITY_ANIMATION_PLAYBACK_TYPE.Once;
    }
    if (normalized === "2" || normalized === "pingpong") {
      return ENTITY_ANIMATION_PLAYBACK_TYPE.PingPong;
    }
  }
  return ENTITY_ANIMATION_PLAYBACK_TYPE.Loop;
}

function parseBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return fallback;
}

function parseFiniteNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function rawHierachyID(
  dto: EntitySnapshotDto,
  partHierarchyById: ReadonlyMap<string, string> | undefined,
): string | null {
  const partId = dto.PartID;
  if (!partId || !partHierarchyById) return null;
  return partHierarchyById.get(partId) ?? null;
}

function hasVec3(map: Readonly<Record<string, number>>): boolean {
  return (
    typeof map.x === "number" &&
    typeof map.y === "number" &&
    typeof map.z === "number"
  );
}

function normalizeHierarchyPath(path: string | null | undefined): string | null {
  if (!path) return null;
  return path.endsWith(",") ? path.slice(0, -1) : path;
}

function findParentSnapshot(
  path: string | null,
  byPath: ReadonlyMap<string, MutableEntitySnapshot>,
  fallbackRoot: MutableEntitySnapshot,
): MutableEntitySnapshot {
  if (!path) return fallbackRoot;
  const segments = path.split(",").filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const parent = byPath.get(segments.join(","));
    if (parent) return parent;
  }
  return fallbackRoot;
}

function kindOf(_dto: EntitySnapshotDto): EntityKind {
  // Without a separate "kind" wire field every snapshot reads as a
  // plain mesh until the inspector ports the rest of the
  // `EntitySettingsData` discriminators (lights / annotations /
  // groups).
  return "mesh";
}

/**
 * Build the `partId → assetId` index from a list of
 * `(assetId, parts)` pairs. Mirrors
 * `ProjectDataLoadingContext.AddAssetPartMapping` /
 * `GetAssetIDByPartID` (`ProjectDataModel_Utils.cs:921-940`).
 */
export function buildPartIdToAssetId(
  assetParts: Iterable<{ assetId: string; parts: ReadonlyArray<{ ID: string }> }>,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const { assetId, parts } of assetParts) {
    for (const p of parts) {
      if (p?.ID) out.set(p.ID, assetId);
    }
  }
  return out;
}
